import { ApiError, apiFetch, jsonOpts, parseJson } from "./client";
import type {
  MarkThreadRead,
  SupportDeescalated,
  SupportDeescalateIn,
  SupportErrorCode,
  SupportMessage,
  SupportMessagesRange,
  SupportReplyAccepted,
  SupportReplyIn,
  SupportReplyStatus,
  SupportThread,
  SupportThreadDetail,
} from "./types";

const THREADS = "/api/support/threads";

const SUPPORT_ERROR_CODES = {
  support_unavailable: true,
  thread_not_found: true,
  reply_not_found: true,
  thread_not_escalated: true,
  already_deescalated: true,
  escalation_mismatch: true,
  idempotency_conflict: true,
  validation_error: true,
  unsupported_content: true,
  channel_window_closed: true,
  not_escalated: true,
  message_after_with_limit: true,
  message_before_not_after: true,
} as const satisfies Record<SupportErrorCode, true>;

function isSupportErrorCode(code: string): code is SupportErrorCode {
  return Object.hasOwn(SUPPORT_ERROR_CODES, code);
}

/** The kit's refusal (or the console being down), by its machine code. */
export function supportErrorCode(error: unknown): SupportErrorCode | null {
  const code = error instanceof ApiError ? error.code : null;
  return code !== null && isSupportErrorCode(code) ? code : null;
}

export async function listThreads(): Promise<SupportThread[]> {
  return parseJson(await apiFetch(THREADS));
}

export async function getThread(
  conversationId: string
): Promise<SupportThreadDetail> {
  return parseJson(await apiFetch(`${THREADS}/${conversationId}`));
}

/** The history after `after` and before `before`, its newest `limit`; all of it without any. */
export async function listMessages(
  conversationId: string,
  range: SupportMessagesRange = {}
): Promise<SupportMessage[]> {
  const params = new URLSearchParams(
    Object.entries(range).flatMap(([key, value]) =>
      value === undefined || value === null ? [] : [[key, String(value)]]
    )
  ).toString();
  return parseJson(
    await apiFetch(
      `${THREADS}/${conversationId}/messages${params ? `?${params}` : ""}`
    )
  );
}

/** One `idempotencyKey` per attempt: the same attempt never sends twice. */
export async function sendReply(
  conversationId: string,
  body: SupportReplyIn,
  idempotencyKey: string
): Promise<SupportReplyAccepted> {
  return parseJson(
    await apiFetch(`${THREADS}/${conversationId}/replies`, {
      ...jsonOpts,
      method: "POST",
      headers: { ...jsonOpts.headers, "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(body),
    })
  );
}

export async function getReply(
  conversationId: string,
  replyId: string
): Promise<SupportReplyStatus> {
  return parseJson(
    await apiFetch(`${THREADS}/${conversationId}/replies/${replyId}`)
  );
}

export async function deescalate(
  conversationId: string,
  body: SupportDeescalateIn
): Promise<SupportDeescalated> {
  return parseJson(
    await apiFetch(`${THREADS}/${conversationId}/deescalate`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

/** Best effort: the BFF answers 204 whatever the kit does with it. */
export async function sendTyping(conversationId: string): Promise<void> {
  await parseJson(
    await apiFetch(`${THREADS}/${conversationId}/typing`, { method: "POST" })
  );
}

export async function markThreadRead(
  conversationId: string,
  body: MarkThreadRead
): Promise<void> {
  await parseJson(
    await apiFetch(`${THREADS}/${conversationId}/read`, {
      ...jsonOpts,
      method: "PUT",
      body: JSON.stringify(body),
    })
  );
}
